import React from 'react';
import { isTimeComponent, componentUsesMarkers } from '../utils/dynamicProps';

/**
 * Memoized grid item component for better performance
 * @param {Object} props - Component props
 * @param {Object} props.transformedComponent - Transformed component data
 * @param {string} props.imageUrl - Image URL
 * @param {Array} props.markersData - Markers data
 * @param {Object} props.planData - Plan data
 * @param {boolean} props.isMainArea - Whether this is in main area
 * @param {Object} props.legacySignage - Legacy signage data
 * @param {Array} props.categoriesData - Categories data for Space Category component
 * @param {string} props.shortName - Short name for dates array
 * @param {Object} props.style - Component style
 * @param {Function} props.renderComponent - Function to render component
 * @returns {JSX.Element} Grid item component
 */
const GridItem = React.memo(({ 
  transformedComponent, 
  imageUrl, 
  markersData, 
  planData, 
  isMainArea, 
  legacySignage, 
  categoriesData,
  shortName,
  style,
  renderComponent
}) => (
  <div 
    className="signage-grid-item"
    style={style}
  >
    <div className="signage-component-wrapper">
      {renderComponent(transformedComponent, imageUrl, markersData, planData, legacySignage, categoriesData, shortName)}
    </div>
  </div>
), (prevProps, nextProps) => {
  // Check what type of component this is
  const usesMarkers = componentUsesMarkers(prevProps.transformedComponent.displayName);
  const isTimeComp = isTimeComponent(prevProps.transformedComponent.displayName);
  
  // Base props comparison
  const basePropsEqual = (
    prevProps.transformedComponent.id === nextProps.transformedComponent.id &&
    prevProps.imageUrl === nextProps.imageUrl &&
    prevProps.isMainArea === nextProps.isMainArea &&
    JSON.stringify(prevProps.style) === JSON.stringify(nextProps.style)
  );
  
  // For time components, they handle their own updates via TimeIsolatedWrapper
  // So we can memoize them based on base props only
  if (isTimeComp) {
    return basePropsEqual;
  }
  
  // For Floor Plan component, include markers in comparison
  if (usesMarkers) {
    return basePropsEqual && JSON.stringify(prevProps.markersData) === JSON.stringify(nextProps.markersData);
  }
  
  // For other components, only check base props
  return basePropsEqual;
});

GridItem.displayName = 'GridItem';

export default GridItem;
